/**
 * Account & Settings › Settings — every setting in one place, opened in the right-hand panel.
 * Route: /settings?section=<section>&tab=<tab>
 * Two groups: YOUR ACCOUNT (every user — profile and password) and COMPANY (needs settings / finance read).
 * Some settings are also reachable next to the pages that use them (Our Company, Tax, Expenses, Service Catalog);
 * both entry points render the same component. Roles are managed on Team › Roles only.
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Paper, Select, Tab, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Business as CompanyIcon, Description as DocumentsIcon, Email as CommunicationIcon, AutoAwesome as AiIcon,
  AccountBalanceWallet as FinanceIcon, AccountBalance as ServicesIcon, Language as PortalIcon, ManageAccounts as AccountIcon,
} from '@mui/icons-material';
import { TAB_SX } from '../../components/common/TabbedPage';
import { isPlatformLevel, isTcAdmin, can } from '../../utils/roleUtils';
import CompanyConfig from '../CompanyConfig';
import TenantSettings from '../Admin/TenantSettings';
import InvoiceConfig from '../InvoiceConfig';
import WorkOrderConfig from '../WorkOrderConfig';
import EmailConfig from '../EmailConfig';
import NotificationSettings from '../NotificationSettings';
import AiSettings from '../ai/AiSettings';
import VatSettings from './VatSettings';
import LockDateSettings from './LockDateSettings';
import ExpenseCategories from './ExpenseCategories';
import AssetSettings from './AssetSettings';
import ServiceProviders from './ServiceProviders';
import PortalFeatureSettings from './PortalFeatureSettings';
import MyAccount from '../account/MyAccount';

// One compact look for every settings page shown here (they were built as full pages with large fields):
// 13px inputs at small-field height, smaller headings and labels, and inner tabs like the rest of the app.
const COMPACT_SX = {
  '& .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline), & .MuiSelect-select': { py: '8.5px' },
  '& .MuiInputBase-multiline': { py: '8.5px' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 9px) scale(1)' },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
  '& .MuiAlert-message': { fontSize: '0.8125rem' },
  '& .MuiTypography-h4': { fontSize: '1.125rem', fontWeight: 600 },
  '& .MuiTypography-h5': { fontSize: '1rem', fontWeight: 600 },
  '& .MuiTypography-h6, & .MuiTypography-subtitle1': { fontSize: '0.875rem', fontWeight: 600 },
  '& .MuiTypography-body1': { fontSize: '0.8125rem' },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiTab-root': { textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, minHeight: 40, py: 0 },
  '& .MuiTab-root .MuiSvgIcon-root': { fontSize: 16 },
  '& .MuiTabs-root': { minHeight: 40 },
  '& .MuiButton-root': { textTransform: 'none', fontSize: '0.8125rem' },
};

const sectionsFor = (currentUser) => {
  const settingsRead = can(currentUser, 'settings', 'read');
  const financeRead = can(currentUser, 'finance', 'read');
  return [
    // Every user — their own profile and password.
    {
      key: 'account', group: 'YOUR ACCOUNT', label: 'My Account', icon: <AccountIcon fontSize="small" />,
      tabs: [{ key: 'profile', label: 'My Account', element: <MyAccount currentUser={currentUser} /> }],
    },
    settingsRead && {
      key: 'company', label: 'Company', icon: <CompanyIcon fontSize="small" />, also: 'Also on Organizations › Our Company',
      tabs: [
        { key: 'details', label: 'Details', element: <CompanyConfig currentUser={currentUser} embedded /> },
        { key: 'branding', label: 'Branding', element: <TenantSettings currentUser={currentUser} embedded /> },
      ],
    },
    settingsRead && {
      key: 'documents', label: 'Documents', icon: <DocumentsIcon fontSize="small" />,
      tabs: [
        { key: 'invoices', label: 'Invoices', element: <InvoiceConfig currentUser={currentUser} /> },
        { key: 'work-orders', label: 'Work Orders', element: <WorkOrderConfig currentUser={currentUser} /> },
      ],
    },
    settingsRead && {
      key: 'communication', label: 'Communication', icon: <CommunicationIcon fontSize="small" />,
      tabs: [
        { key: 'email', label: 'Email', element: <EmailConfig currentUser={currentUser} /> },
        { key: 'notifications', label: 'Notifications', element: <NotificationSettings currentUser={currentUser} /> },
      ],
    },
    // Typing-center only: what clients get in their portal (14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.1).
    settingsRead && !isPlatformLevel(currentUser?.role) && {
      key: 'portal', label: 'Client Portal', icon: <PortalIcon fontSize="small" />,
      tabs: [
        { key: 'features', label: 'Features', element: <PortalFeatureSettings currentUser={currentUser} /> },
      ],
    },
    settingsRead && {
      key: 'ai', label: 'AI', icon: <AiIcon fontSize="small" />,
      tabs: [
        { key: 'settings', label: 'Settings', element: <AiSettings currentUser={currentUser} /> },
      ],
    },
    financeRead && {
      key: 'finance', label: 'Finance', icon: <FinanceIcon fontSize="small" />, also: 'Also on Tax, Expenses and Fixed Assets',
      tabs: [
        { key: 'vat', label: 'VAT', element: <VatSettings currentUser={currentUser} /> },
        { key: 'expense-categories', label: 'Expense Categories', element: <ExpenseCategories currentUser={currentUser} /> },
        { key: 'fixed-assets', label: 'Fixed Assets', element: <AssetSettings currentUser={currentUser} /> },
        ...(isTcAdmin(currentUser?.role) ? [{ key: 'lock-date', label: 'Lock date', element: <LockDateSettings /> }] : []),
      ],
    },
    settingsRead && {
      key: 'services', label: 'Services', icon: <ServicesIcon fontSize="small" />, also: 'Also on Service Catalog',
      tabs: [
        { key: 'providers', label: 'Service Providers', element: <ServiceProviders currentUser={currentUser} /> },
      ],
    },
  ].filter(Boolean);
};

const SettingsHub = ({ currentUser }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [params, setParams] = useSearchParams();
  const sections = sectionsFor(currentUser);
  if (sections.length === 0) return null;
  // No ?section: admins land on Company, everyone else on their own account.
  const section = sections.find((s) => s.key === params.get('section'))
    || sections.find((s) => s.key !== 'account') || sections[0];
  const tab = section.tabs.find((t) => t.key === params.get('tab')) || section.tabs[0];

  const go = (sectionKey, tabKey) => setParams({ section: sectionKey, ...(tabKey ? { tab: tabKey } : {}) }, { replace: true });

  return (
    <Box sx={{ width: 0, minWidth: '100%', display: 'flex', gap: 2, flexDirection: narrow ? 'column' : 'row', alignItems: 'flex-start' }}>
      {narrow ? (
        <Select size="small" value={section.key} onChange={(e) => go(e.target.value)}
          sx={{ minWidth: 220, '& .MuiSelect-select': { fontSize: '0.8125rem' } }}>
          {sections.map((s) => <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.8125rem' }}>{s.label}</MenuItem>)}
        </Select>
      ) : (
        <Paper variant="outlined" sx={{ width: 210, flexShrink: 0, position: 'sticky', top: 16 }}>
          <List dense disablePadding sx={{ py: 0.5 }}>
            {sections.map((s, i) => (
              <React.Fragment key={s.key}>
              {(s.group || 'COMPANY') !== ((sections[i - 1] && (sections[i - 1].group || 'COMPANY')) || '') && (
                <ListSubheader disableSticky sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', lineHeight: '28px', bgcolor: 'transparent', mt: i ? 0.5 : 0 }}>
                  {s.group || 'COMPANY'}
                </ListSubheader>
              )}
              <ListItemButton selected={s.key === section.key} onClick={() => go(s.key)}
                sx={{ mx: 0.5, borderRadius: 1, '&.Mui-selected': { color: 'secondary.main' } }}>
                <ListItemIcon sx={{ minWidth: 32, color: s.key === section.key ? 'secondary.main' : 'text.secondary' }}>{s.icon}</ListItemIcon>
                <ListItemText primary={s.label} primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: s.key === section.key ? 600 : 400 }} />
              </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      <Box sx={{ flex: 1, minWidth: 0, width: narrow ? '100%' : 'auto' }}>
        {section.tabs.length > 1 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab.key} onChange={(e, v) => go(section.key, v)} variant="scrollable" scrollButtons="auto"
              sx={{ minHeight: 40, flex: 1 }}>
              {section.tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />)}
            </Tabs>
            {section.also && (
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', whiteSpace: 'nowrap', pr: 1 }}>{section.also}</Typography>
            )}
          </Box>
        ) : section.also ? (
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', textAlign: 'right', mb: 1 }}>{section.also}</Typography>
        ) : null}
        <Box key={`${section.key}-${tab.key}`} sx={COMPACT_SX}>{tab.element}</Box>
      </Box>
    </Box>
  );
};

export default SettingsHub;
